import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import SectionHeader from '../components/SectionHeader';
import Pill from '../components/Pill';
import { fmt } from '../utils/format';
import { printInvoices } from '../utils/printInvoices';

const trunc = (str, len) => {
  if (!str) return '—';
  return str.length > len ? str.slice(0, len) + '…' : str;
};

const stageColor = (stage) => {
  if (!stage) return 'muted';
  const s = stage.toLowerCase();
  if (s.includes('fully picked'))      return 'green';
  if (s.includes('new'))               return 'blue';
  if (s.includes('awaiting'))          return 'gold';
  if (s.includes('warehouse'))         return 'blue';
  return 'muted';
};

// ── PoOpenOrders Page ─────────────────────────────────────────────────────────
// For a given PO, every undispatched (non-VOID) sales order line carrying one
// of the PO's item codes. One row per line; selection and printing are per
// sales order, same invoice print as Smart Buyer Dispatch.
const PoOpenOrders = () => {
  const navigate = useNavigate();
  const [params, setParams]     = useSearchParams();
  const poRef                   = params.get('po') || '';
  const [input, setInput]       = useState(poRef);
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [selected, setSelected] = useState(new Set());
  const [printing, setPrinting] = useState(false);
  const [submitted, setSubmitted] = useState(new Set());

  const load = useCallback(async () => {
    if (!poRef) { setRows([]); return; }
    setLoading(true);
    setError('');
    setSelected(new Set());
    const { data, error: err } = await supabase.rpc('get_po_open_orders', { p_po_ref: poRef });
    if (err) setError(err.message);
    setRows(data || []);
    setLoading(false);
  }, [poRef]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (e) => {
    e.preventDefault();
    const v = input.trim().toUpperCase();
    setInput(v);
    setParams(v ? { po: v } : {});
  };

  const orderRefs = useMemo(() => [...new Set(rows.map(r => r.order_ref))], [rows]);
  const allSelected  = orderRefs.length > 0 && orderRefs.every(r => selected.has(r));
  const someSelected = orderRefs.some(r => selected.has(r));

  const handleToggle = (orderRef) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(orderRef)) next.delete(orderRef); else next.add(orderRef);
      return next;
    });
  };

  const handleToggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(orderRefs));
  };

  // Same queue as Smart Buyer Dispatch's FPHold -- the stage change is applied
  // to Cin7 from sales_order_stage_updates, not written here directly.
  const handleFPHold = async (e, orderRef) => {
    e.stopPropagation();
    const { error: err } = await supabase.from('sales_order_stage_updates').insert({
      order_ref: orderRef,
      stage:     'Fully Picked - Hold',
    });
    if (err) { alert(`Failed to queue ${orderRef}: ${err.message}`); return; }
    setSubmitted(prev => new Set(prev).add(orderRef));
  };

  const handlePrint = async () => {
    if (!selected.size) return;
    setPrinting(true);
    await printInvoices([...selected]);
    setPrinting(false);
  };

  return (
    <main className="app-main">
      <div className="page-nav">
        <button className="back-btn" onClick={() => navigate('/dispatch-tools')}>← Dispatch Tools</button>
        <span className="page-nav__title">PO — Open Orders</span>
      </div>

      <section className="section">
        <SectionHeader
          title={poRef ? `Open Orders for ${poRef}` : 'Open Orders for PO'}
          count={orderRefs.length}
          onRefresh={load}
          loading={loading}
          actions={
            <>
              <form onSubmit={handleSearch} style={{ display: 'inline-flex', gap: 6, marginRight: 8 }}>
                <input
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder="PO-385261"
                  style={{
                    background: 'var(--bg3)', color: 'var(--white)',
                    border: '1px solid var(--border2)', borderRadius: 4,
                    padding: '4px 10px', fontFamily: 'var(--font-body)', fontSize: 12, width: 130,
                  }}
                />
                <button type="submit" className="action-btn action-btn--reset">Search</button>
              </form>
              <button
                className="action-btn action-btn--close"
                onClick={handlePrint}
                disabled={printing || selected.size === 0}
                style={{ marginRight: 8, opacity: selected.size === 0 ? 0.4 : 1 }}
              >
                {printing ? '…' : `🖨 Print${selected.size > 0 ? ` ${selected.size} Invoice${selected.size !== 1 ? 's' : ''}` : ''}`}
              </button>
            </>
          }
        />
        <p className="section__desc">
          Every undispatched sales order (VOID excluded, any stage) holding an item code that's on
          this PO. One row per order line; ticking a row selects the whole sales order for printing.
          {selected.size > 0 && <strong style={{ color: 'var(--green-light)', marginLeft: 8 }}>{selected.size} selected</strong>}
        </p>

        {error && <div className="empty-row" style={{ color: 'var(--red)' }}>{error}</div>}
        {!poRef ? (
          <div className="empty-row">Enter a PO number to search</div>
        ) : loading ? (
          <div className="loading-row">Loading...</div>
        ) : !rows.length ? (
          <div className="empty-row">No open orders for any item on {poRef}</div>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Sales Order</th>
                  <th style={{ width: 90 }}></th>
                  <th style={{ width: 36, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                      onChange={handleToggleAll}
                      title="Select all"
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                  <th>Item Code</th>
                  <th>Item Name</th>
                  <th>Stage</th>
                  <th>Customer</th>
                  <th>Created</th>
                  <th className="num">PO Qty</th>
                  <th className="num">Ordered</th>
                  <th className="num">Moved</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const isSelected = selected.has(r.order_ref);
                  // One button per order, on its first line.
                  const isFirstLine = i === 0 || rows[i - 1].order_ref !== r.order_ref;
                  const isOnHold    = r.stage === 'Fully Picked - Hold';
                  return (
                    <tr
                      key={`${r.order_ref}-${r.item_code}-${i}`}
                      className={`data-row ${isSelected ? 'data-row--selected' : ''}`}
                      onClick={() => handleToggle(r.order_ref)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td><span className="code-badge">{r.order_ref}</span></td>
                      <td onClick={e => e.stopPropagation()}>
                        {isFirstLine && !isOnHold && (
                          submitted.has(r.order_ref)
                            ? <span style={{ fontSize: 10, color: 'var(--muted)', fontStyle: 'italic' }}>Submitted</span>
                            : <button
                                onClick={e => handleFPHold(e, r.order_ref)}
                                style={{
                                  fontSize: 10, padding: '2px 8px', cursor: 'pointer',
                                  background: 'rgba(148,103,60,0.25)', color: '#c8922a',
                                  border: '1px solid rgba(200,146,42,0.35)', borderRadius: 4, fontWeight: 600,
                                  letterSpacing: '0.03em',
                                }}
                              >
                                FPHold
                              </button>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggle(r.order_ref)}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                      <td><span className="code-badge code-badge--sm">{r.item_code}</span></td>
                      <td className="product-name">{trunc(r.item_name, 40)}</td>
                      <td><Pill label={r.stage || '—'} color={stageColor(r.stage)} /></td>
                      <td>{trunc(r.customer_name, 20)}</td>
                      <td className="muted">{r.created_date ? new Date(r.created_date).toLocaleDateString('en-NZ') : '—'}</td>
                      <td className="num">{fmt(r.po_qty)}</td>
                      <td className="num">{fmt(r.item_qty)}</td>
                      <td className="num">{fmt(r.item_qty_moved)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
};

export default PoOpenOrders;
